import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "./store";
import { FaultLogStatus } from "@/shared/Enums";
import { AdvancedSearchFilterItem, CreateEmptyAdvancedSearchFilterItemList } from "@/shared/models/advanced-search/AdvancedSearchFilterItem";

export interface IDashboardState {
  // Check is the common data is loaded and we can do the search
  isInitialized: boolean;
  filterRegionList: string[];
  filterProductList: string[];
  filterTerminalList: string[];
  filterStatusList: FaultLogStatus[];
  filterStartTime: string;
  filterEndTime: string;
  advancedFilters: AdvancedSearchFilterItem[];
  openFilter: boolean;
  openAdvancedQuery: boolean;
}

const initialState: IDashboardState = {
  isInitialized: false,
  filterRegionList: [] as string[],
  filterProductList: [] as string[],
  filterTerminalList: [] as string[],
  filterStatusList: [FaultLogStatus.Open, FaultLogStatus.Draft, FaultLogStatus.NotToBeRepaired],
  filterStartTime: "",
  filterEndTime: "",
  advancedFilters: CreateEmptyAdvancedSearchFilterItemList(),
  openFilter: true,
  openAdvancedQuery: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialState,
  reducers: {
    setInitializationData(state, action) {
      state.isInitialized = action.payload.isInitialized;
      state.filterRegionList = action.payload.filterRegionList;
      state.filterProductList = action.payload.filterProductList;
      state.filterTerminalList = action.payload.filterTerminalList;
    },
    setFilterRegionList(state, action) {
      state.filterRegionList = action.payload;
    },
    setFilterProductList(state, action) {
      state.filterProductList = action.payload;
    },
    setFilterTerminalList(state, action) {
      state.filterTerminalList = action.payload;
    },
    setFilterStatusList(state, action) {
      state.filterStatusList = action.payload;
    },
    setFilterStartTime(state, action) {
      state.filterStartTime = action.payload;
    },
    setFilterEndTime(state, action) {
      state.filterEndTime = action.payload;
    },
    setAdvancedFilters(state, action) {
      state.advancedFilters = action.payload;
    },
    setOpenFilter(state, action) {
      state.openFilter = action.payload;
    },
    setOpenAdvancedQuery(state, action) {
      state.openAdvancedQuery = action.payload;
    },
    resetFilters(state, action) {
      state.isInitialized = action.payload.isInitialized;
      state.filterRegionList = action.payload.filterRegionList;
      state.filterProductList = action.payload.filterProductList;
      state.filterTerminalList = action.payload.filterTerminalList;
      state.advancedFilters = CreateEmptyAdvancedSearchFilterItemList();
      state.filterStatusList = [FaultLogStatus.Open, FaultLogStatus.Draft, FaultLogStatus.NotToBeRepaired];
      state.filterStartTime = "";
      state.filterEndTime = "";
      state.openFilter = true;
      state.openAdvancedQuery = false;
    },
  },
});

export const {
  setInitializationData,
  setFilterRegionList,
  setFilterProductList,
  setFilterTerminalList,
  setFilterStatusList,
  setFilterStartTime,
  setFilterEndTime,
  setAdvancedFilters,
  setOpenFilter,
  setOpenAdvancedQuery,
  resetFilters,
} = dashboardSlice.actions;
export const DashboardReducer = dashboardSlice.reducer;

// Selector
export const selectIsInitialized = (state: RootState) => state.dashboard.isInitialized;
export const selectFilterRegionList = (state: RootState) => state.dashboard.filterRegionList;
export const selectFilterProductList = (state: RootState) => state.dashboard.filterProductList;
export const selectFilterTerminalList = (state: RootState) => state.dashboard.filterTerminalList;
export const selectFilterStatusList = (state: RootState) => state.dashboard.filterStatusList;
export const selectFilterStartTime = (state: RootState) => state.dashboard.filterStartTime;
export const selectFilterEndTime = (state: RootState) => state.dashboard.filterEndTime;
export const selectAdvancedFilters = (state: RootState) => state.dashboard.advancedFilters;
export const selectOpenFilter = (state: RootState) => state.dashboard.openFilter;
export const selectOpenAdvancedQuery = (state: RootState) => state.dashboard.openAdvancedQuery;
